import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { isPast } from 'date-fns';

import { useMonetaryAmount } from '@sorare/core/src/hooks/useMonetaryAmount';

import type { useIsPrimaryBuyLive_anyCard } from './__generated__/useIsPrimaryBuyLive.graphql.ts';

export const isPrimaryBuyLive = (
  card: Nullable<useIsPrimaryBuyLive_anyCard>
) => {
  const { livePrimaryOffer, user } = card || {};
  const { endDate, price, cards } = livePrimaryOffer || {};

  const ended = !!endDate && isPast(endDate);

  // Checking the token has an owner is mandatory as the
  // PrimaryOffer can ends way after the token has been sold
  const isOnSale = !!livePrimaryOffer && !user && !ended;

  const isPrimaryBuy = cards?.length === 1;

  return isOnSale && !!price && !!endDate && isPrimaryBuy;
};

export const useIsPrimaryBuyLive = (
  card: Nullable<useIsPrimaryBuyLive_anyCard>
) => {
  const { livePrimaryOffer } = card || {};
  const endDate = livePrimaryOffer?.endDate;
  const price = livePrimaryOffer?.price;
  const { toMonetaryAmount } = useMonetaryAmount();

  if (isPrimaryBuyLive(card) && price && endDate) {
    return {
      endDate,
      ended: isPast(endDate),
      price: toMonetaryAmount(price),
    };
  }
  return undefined;
};

useIsPrimaryBuyLive.fragments = {
  anyCard: gql`
    fragment useIsPrimaryBuyLive_anyCard on AnyCardInterface {
      slug
      livePrimaryOffer @include(if: $withPrimaryOffer) {
        id
        price {
          ...useMonetaryAmount_monetaryAmount
        }
        endDate
        cards: anyCards {
          slug
        }
      }
      user {
        slug
      }
    }
    ${useMonetaryAmount.fragments.monetaryAmount}
  ` as TypedDocumentNode<useIsPrimaryBuyLive_anyCard>,
};
